/**
 * buildQueryClient — constructs the TanStack Query client with global
 * error handling for the v0.5.0 auth/error contract.
 *
 * Behavior:
 *   - On TerminalAuthError from any query OR mutation: log + signOut().
 *     The reload returns the user to the Amplify Authenticator's login.
 *   - On UpstreamUnavailableError: TanStack's default 3-retry behavior
 *     handles transient 503s; no special override needed here.
 *   - On AccessDeniedError: passes through to the consuming hook's
 *     `error` slot, where route-level handlers render the appropriate
 *     access-denied UI.
 *
 * Mounted once at module init in main.tsx.
 */
import { QueryCache, QueryClient } from "@tanstack/react-query";
import { signOut } from "./tokenProvider";
import { isTerminalAuth } from "./errors";

export function buildQueryClient(): QueryClient {
  const queryCache = new QueryCache({
    onError: (error) => {
      if (isTerminalAuth(error)) {
        console.warn("[auth] terminal — signing out:", error.code);
        // Fire-and-forget; the reload returns to the login form.
        void signOut({ reload: true });
      }
    },
  });

  return new QueryClient({
    queryCache,
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        refetchOnWindowFocus: false,
        retry: (failureCount, error) => {
          // Don't retry on auth errors — they're terminal or already
          // handled (refresh-and-retry was inside the interceptor).
          if (isTerminalAuth(error)) return false;
          // 403 isn't retryable.
          if ((error as { name?: string })?.name === "AccessDeniedError") return false;
          // 503 / network errors: TanStack default (3 retries).
          return failureCount < 3;
        },
      },
      mutations: {
        retry: false,
      },
    },
  });
}
